import "../../UserCollectedData.css";
import "./EditSearch.css";
import { IconButton, Modal } from "@mui/material";
import EnforcementAppealsAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementAppealsAdvancedSearch";
import Box from "@mui/material/Box";
import Board from "@publicPortal/components/board/Board";
import EnforcementAppealsSearchState from "@publicPortal/pages/search/EnforcementAppeal/EnforcementAppealsSearchState";
import EnforcementAppealsService from "@publicPortal/services/EnforcementAppealsService";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import { IsStringNotEmptyValidator, ValidationResult } from "@shared/mirage/validators/IsStringNotEmptyValidator";
import { lazy, memo, Suspense, useCallback, useMemo, useState } from "react";
import UserCollectedDataHelper from "../../UserCollectedDataHelper";
import { EditSavedSearch } from "./PlanningApplicationEditSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import EnforcementAppealsSimpleSearch from "@publicPortal/pages/search/EnforcementAppeal/EnforcementAppealsSimpleSearch";
import MapFallback from "@mirage/components/MapView/components/MapFallback";
import SearchActions from "@publicPortal/pages/search/SearchActions";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { GAIA_DRAW_MODE, TileLayerOptions } from "@mirage/components/gaiaMap/GaiaMap";

const GaiaMap = lazy(() =>
    import("@mirage/components/gaiaMap/GaiaMap").then((module) => {
        return { default: module.GaiaMap };
    })
);

const EnforcementAppealEditSearch = memo((props: EditSavedSearch) => {
    const service = new EnforcementAppealsService();
    const initialValues = props.criteria.jsonSearchParameters && JSON.parse(props.criteria.jsonSearchParameters);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const { initialSearchState, initialFilterState } = PublicPortalState.enforcementAppealInitialSearchState();
    const initSearchState: EnforcementAppealsSearchState = {
        ...initialSearchState,
        filter: {
            ...initialFilterState,
            ...initialValues,
        },
    };
    const [state, setState] = useState<EnforcementAppealsSearchState>(initSearchState);
    const [searchName, setSearchName] = useState("");
    const [searchNameValidation, setSearchNameValidation] = useState<ValidationResult>({ passed: true, error: "" });

    const handleSearchName = (value: string) => {
        const validationResult = IsStringNotEmptyValidator(value, "Enter Search name");
        setSearchNameValidation(validationResult);
        setSearchName(value);
    };

    useMemo(() => {
        setSearchName(props.criteria.searchName);
        setSearchNameValidation({ passed: true, error: "" });
        setState(initSearchState);
        fetchPicklistOptions();
    }, [props.criteria, props.closeModal]);

    const setStateModifier = useCallback(
        (modifier: Partial<EnforcementAppealsFilter>) => {
            setState((prev) => {
                return { ...prev, filter: { ...prev.filter, ...modifier } };
            });
        },
        [setState]
    );

    async function handleSave() {
        if (searchNameValidation.passed) {
            await props.saveEditSearch(props.criteria.id, searchName, props.criteria.searchType, state.filter);
        }
    }
    const onChange = useCallback(
        (geometry: string | null) => {
            setStateModifier({ geometry: geometry || "" });
        },
        [setStateModifier]
    );

    const formActions = <SearchActions onSubmitHandler={handleSave} onCancelHandler={props.closeModal} customSubmitName={"Save"} customCancelName={"Cancel"} />;
    const masterLayer: TileLayerOptions | undefined = useMemo(
        () =>
            clientSettings
                ? {
                      layerUrl: clientSettings.MASTER_LAYER_URL,
                      attribution: clientSettings.MASTER_LAYER_ATTRIBUTION,
                  }
                : undefined,
        [clientSettings]
    );
    const enabledToolTypes = useMemo(() => [GAIA_DRAW_MODE.POLYGON], []);

    function getForm() {
        switch (initialValues?.searchType) {
            case "Simple": {
                return <EnforcementAppealsSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementAppealsAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return (
                    <Suspense fallback={<MapFallback />}>
                        <GaiaMap geometryJson={state.filter.geometry} onChange={onChange} tileLayer={masterLayer} height={500} enabledToolTypes={enabledToolTypes} />
                    </Suspense>
                );
            }
            default:
                return <EnforcementAppealsAdvancedSearch state={state} setter={setStateModifier} />;
        }
    }

    function getBoardStyles() {
        if (initialValues?.searchType === "Map") {
            const mapBoardStyles = {
                width: "75%",
            };

            return mapBoardStyles;
        }
    }

    const boardSize = initialValues?.searchType === "Advanced" ? "big" : "small";

    return (
        <>
            <Modal open={props.isOpen}>
                <Box height={"100%"}>
                    <Board id={boardSize === "big" ? "editSearch__bigBoard" : "editSearch__smallBoard"} size={boardSize} style={getBoardStyles()}>
                        <Box className="editSearch__headerContainer">
                            {UserCollectedDataHelper.getSearchName(searchName, handleSearchName, searchNameValidation)}
                            <IconButton onClick={props.closeModal} className="close-saved-search-modal-button">
                                <FontAwesomeIcon icon={faXmark} style={PublicPortalTheme.icons.sizeM} />
                            </IconButton>
                        </Box>
                        {getForm()}
                        {formActions}
                    </Board>
                </Box>
            </Modal>
        </>
    );

    async function fetchPicklistOptions() {
        service.getFilteringLists().then((filter) => {
            setState((prev) => {
                return { ...prev, decisionTypes: filter.decisionTypes || [], wards: filter.wards || [], parishes: filter.parishes || [] };
            });
        });
    }
});

export default EnforcementAppealEditSearch;
